import { useCallback, useEffect, useRef } from "react";

const HOLD_MS = 280;
const MOVE_TOLERANCE = 8; // px of pointer travel that turns a press into a scroll

/**
 * Tap and press-and-hold on a list row. A tap (pointer down and up without moving) calls
 * onTap; holding for a moment calls onHold(true) and, on release, onHold(false). Scrolling
 * (the pointer moves, or the browser takes it over) cancels both. Keyboard activation
 * counts as a tap. Returns the props to spread on the row's button.
 */
export default function usePressPreview({ onTap, onHold }) {
  const press = useRef(null); // the press in progress: its timer, listeners and callbacks

  const finish = useCallback((released) => {
    const current = press.current;
    if (!current) return;
    press.current = null;
    clearTimeout(current.timer);
    window.removeEventListener("pointermove", current.onMove);
    window.removeEventListener("pointerup", current.onUp);
    window.removeEventListener("pointercancel", current.onCancel);
    if (current.held) current.onHold(false);
    else if (released && !current.moved) current.onTap();
  }, []);

  useEffect(() => () => finish(false), [finish]);

  const onPointerDown = (event) => {
    if (event.button != null && event.button > 0) return;
    finish(false);
    const current = { onTap, onHold, held: false, moved: false, x: event.clientX || 0, y: event.clientY || 0 };
    current.onMove = (move) => {
      if (Math.abs((move.clientX || 0) - current.x) <= MOVE_TOLERANCE && Math.abs((move.clientY || 0) - current.y) <= MOVE_TOLERANCE) return;
      current.moved = true;
      if (!current.held) clearTimeout(current.timer);
    };
    current.onUp = () => finish(true);
    current.onCancel = () => {
      current.moved = true;
      finish(false);
    };
    current.timer = setTimeout(() => {
      if (current.moved) return;
      current.held = true;
      current.onHold(true);
    }, HOLD_MS);
    window.addEventListener("pointermove", current.onMove);
    window.addEventListener("pointerup", current.onUp);
    window.addEventListener("pointercancel", current.onCancel);
    press.current = current;
  };

  // Enter or Space on the button: no pointer was involved, so the click stands alone
  const onClick = (event) => {
    if (event.detail === 0) onTap();
  };

  // a long press on a phone would otherwise select the text or open a menu
  const onContextMenu = (event) => event.preventDefault();

  return { onPointerDown, onClick, onContextMenu };
}
